import { DissectedErrorGroups, HandledApplicationError, LogType, logError } from '../../..'
import { ApiResponse } from '../../../types/Response'
import { HandleErrorCallback, HandleErrorCallbackProps } from '../types/HandleErrorCallback'

export const defaultDissectedErrorHandler: HandleErrorCallback = async <TApiResponse extends ApiResponse>({
    error,
    apiResponseState,
    formId,
    throwWhenNotSuccessful,
}: HandleErrorCallbackProps<TApiResponse>): Promise<TApiResponse | null> => {
    const unhandledPrefix = 'UNHANDLED:'
    // TODO: Handle Unexpected Error
    switch (error.errorGroup) {
        case DissectedErrorGroups.RESPONSE_NOT_OK: {
            if (error.statusCode === 401) {
                logError(`${unhandledPrefix} Unauthorized (401) response received.`, {
                    caller: 'defaultDissectedErrorHandler',
                    logType: LogType.RESPONSE_MESSAGE,
                })

                // TODO: throw HandledApplicationError when all applications have a mechanism to catch HandledApplicationErrors and act upon them
            }
            if (throwWhenNotSuccessful) {
                throw new HandledApplicationError('API response returned a non-OK status.', {
                    formId: formId ?? 'no-form-id-provided',
                    apiResponse: error.apiResponse,
                })
            }
            return apiResponseState
        }
        case DissectedErrorGroups.IS_SUCCESS_FALSE: {
            // Handle the case where isSuccess is false
            const hasValidationErrors = !!error.apiResponse?.validationErrors?.length
            if (formId && hasValidationErrors) {
                throw new HandledApplicationError('API response indicates failure with validation errors.', {
                    formId,
                    apiResponse: error.apiResponse,
                    validationErrors: error.apiResponse?.validationErrors!,
                })
            }
            if (formId) {
                throw new HandledApplicationError(
                    'API response indicates failure WITHOUT validation errors but a formId was provided.',
                    {
                        formId,
                        apiResponse: error.apiResponse,
                        validationErrors: [],
                    },
                )
            }
            logError(`${unhandledPrefix} API response indicates failure with isSuccess false.`, {
                caller: 'defaultDissectedErrorHandler',
                logType: LogType.RESPONSE_MESSAGE,
            })
            if (throwWhenNotSuccessful) {
                throw new HandledApplicationError('API response indicates failure with isSuccess false.', {
                    apiResponse: error.apiResponse,
                    formId: formId ?? 'no-form-id-provided',
                })
            }
            return apiResponseState
        }
        case DissectedErrorGroups.ORIGIN_FRONT_END: {
            if (!!formId) {
                throw new HandledApplicationError('Front-end error occurred during API response processing.', {
                    formId,
                })
            }
            logError(`${unhandledPrefix} Front-end error occurred during API response processing.`, {
                caller: 'defaultDissectedErrorHandler',
                logType: LogType.UNEXPECTED,
            })
            return apiResponseState
        }
        case DissectedErrorGroups.NO_DATA_WHEN_DATA_WAS_EXPECTED: {
            // Handle the case where data was expected but not present
            logError(
                `${unhandledPrefix} Error. Data was expected, but not found, while \`ApiResponse.isSuccess\` was \`true\``,
                {
                    caller: 'defaultDissectedErrorHandler',
                    logType: LogType.UNEXPECTED,
                },
            )
            return apiResponseState
        }
        case DissectedErrorGroups.UNEXPECTED_ERROR_PARSING_RESPONSE_OBJECT: {
            // Handle unexpected errors during response parsing
            logError(`${unhandledPrefix} Unexpected error occurred while parsing the response object.`, {
                caller: 'defaultDissectedErrorHandler',
                logType: LogType.UNEXPECTED,
            })
            return apiResponseState
        }
        default: {
            // Handle any other unexpected errors
            console.error('An unexpected error occurred.', error)
            return apiResponseState
        }
    }
}
